/**
 * ZAPHUB 2.1 — BillingPage
 *
 * Gestión de suscripción y pagos con Revolut.
 * Ruta: /dashboard/billing
 *
 * - Muestra plan actual + uso del mes
 * - Cards de planes con botón de upgrade
 * - Historial de pagos
 * - Checkout Revolut embebido
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  CheckCircle,
  Zap,
  Crown,
  Building2,
  CreditCard,
  Loader2,
  AlertCircle,
  TrendingUp,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import api from '../services/apiClient';

interface Plan {
  id: string;
  name: string;
  priceEur: number;
  pricePerExecution: number;
  maxWorkflows: number;
  maxExecutionsMonth: number;
  features: string[];
}

interface Subscription {
  planId: string;
  plan: Plan;
  status: string;
  currentPeriodEnd?: string;
  executionsThisMonth: number;
  executionsLimit: number;
}

interface Payment {
  id: string;
  planId: string;
  amountEur: number;
  status: string;
  description: string;
  paidAt?: string;
  createdAt: string;
}

interface BillingSummary {
  subscription: Subscription;
  monthlyUsageEur: number;
  payments: Payment[];
  plans: Plan[];
}

const PLAN_ICONS: Record<string, React.ReactNode> = {
  starter: <Zap className="w-6 h-6 text-blue-500" />,
  pro:     <Crown className="w-6 h-6 text-purple-500" />,
  enterprise: <Building2 className="w-6 h-6 text-amber-500" />,
};

const PLAN_COLORS: Record<string, string> = {
  starter: 'border-blue-200 bg-blue-50/30',
  pro:     'border-purple-300 bg-purple-50/30 ring-2 ring-purple-400',
  enterprise: 'border-amber-200 bg-amber-50/30',
};

function formatDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

function UsageBar({ used, limit }: { used: number; limit: number }) {
  if (limit <= 0) return <div className="text-xs text-green-600 font-medium">∞ ilimitado</div>;
  const pct = Math.min((used / limit) * 100, 100);
  const color = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-400' : 'bg-green-500';
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{used.toLocaleString()} ejecuciones</span>
        <span>{limit.toLocaleString()} límite</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function BillingPage() {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/billing/summary');
      setSummary(data.data);
    } catch {
      setError('No se pudo cargar la información de billing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Handle return from Revolut checkout
  useEffect(() => {
    if (searchParams.get('success') === '1') {
      setTimeout(load, 2000); // Give webhook time to process
    }
  }, [searchParams, load]);

  const handleUpgrade = async (planId: string) => {
    setUpgrading(planId);
    setError('');
    try {
      const { data } = await api.post('/billing/upgrade', { planId: planId.toUpperCase() });
      if (data.data?.checkoutUrl) {
        // Redirect to Revolut checkout
        window.location.href = data.data.checkoutUrl;
      } else {
        // Free plan downgrade
        await load();
      }
    } catch (err: any) {
      setError(err?.message || 'Error al procesar el pago');
    } finally {
      setUpgrading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  const currentPlanId = summary?.subscription?.planId?.toLowerCase() || 'starter';
  const sub = summary?.subscription;

  return (
    <div className="space-y-6 max-w-5xl mx-auto p-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Facturación</h1>
          <p className="text-gray-500 text-sm mt-1">Gestiona tu plan y método de pago</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      {/* Success banner */}
      {searchParams.get('success') === '1' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800 text-sm font-medium">¡Pago completado! Tu plan ha sido actualizado.</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Current plan status */}
      {sub && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {PLAN_ICONS[currentPlanId]}
              <div>
                <div className="font-semibold text-gray-900 text-lg">{sub.plan.name}</div>
                <div className="text-xs text-gray-400">
                  {sub.plan.priceEur === 0 ? 'Gratis' : `€${sub.plan.priceEur}/mes`}
                  {sub.currentPeriodEnd && ` · Renueva ${formatDate(sub.currentPeriodEnd)}`}
                </div>
              </div>
            </div>
            <Badge className={sub.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
              {sub.status}
            </Badge>
          </div>
          <UsageBar used={sub.executionsThisMonth} limit={sub.executionsLimit} />
          {summary && summary.monthlyUsageEur > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
              <TrendingUp className="w-4 h-4" />
              Cargos variables este mes: <span className="font-semibold text-gray-900">€{summary.monthlyUsageEur.toFixed(2)}</span>
            </div>
          )}
        </Card>
      )}

      {/* Plans grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Planes disponibles</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(summary?.plans || []).map(plan => {
            const isCurrentPlan = plan.id === currentPlanId;
            const planKey = plan.id.toLowerCase();
            return (
              <Card key={plan.id} className={`p-5 border-2 relative ${PLAN_COLORS[planKey] || 'border-gray-200'}`}>
                {planKey === 'pro' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-purple-600 text-white text-xs px-3">MÁS POPULAR</Badge>
                  </div>
                )}

                <div className="flex items-center gap-2 mb-3">
                  {PLAN_ICONS[planKey]}
                  <span className="font-bold text-gray-900 text-lg">{plan.name}</span>
                </div>

                <div className="mb-4">
                  {plan.priceEur === 0 ? (
                    <span className="text-3xl font-bold text-gray-900">Gratis</span>
                  ) : (
                    <div>
                      <span className="text-3xl font-bold text-gray-900">€{plan.priceEur}</span>
                      <span className="text-gray-500 text-sm">/mes</span>
                    </div>
                  )}
                  {plan.pricePerExecution > 0 && (
                    <div className="text-xs text-gray-400 mt-0.5">+ €{plan.pricePerExecution}/ejecución extra</div>
                  )}
                </div>

                <ul className="space-y-1.5 mb-5">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <Button disabled className="w-full" variant="outline">
                    Plan actual
                  </Button>
                ) : (
                  <Button
                    className={`w-full ${planKey === 'pro' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                    onClick={() => handleUpgrade(plan.id.toUpperCase())}
                    disabled={upgrading !== null}
                  >
                    {upgrading === plan.id.toUpperCase() ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...</>
                    ) : (
                      <><CreditCard className="w-4 h-4 mr-2" />
                        {plan.priceEur === 0 ? 'Cambiar a Starter' : `Contratar ${plan.name}`}
                      </>
                    )}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
        <p className="text-xs text-center text-gray-400 mt-3">
          Pagos procesados de forma segura por <ExternalLink className="w-3 h-3 inline" /> Revolut Business
        </p>
      </div>

      {/* Payment history */}
      {summary && summary.payments.length > 0 && (
        <Card className="p-5">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gray-400" />
            Historial de pagos
          </h2>
          <div className="divide-y">
            {summary.payments.map(p => (
              <div key={p.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-800">{p.description}</div>
                  <div className="text-xs text-gray-400">{formatDate(p.createdAt)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900">€{p.amountEur.toFixed(2)}</span>
                  <Badge className={
                    p.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                    p.status === 'FAILED'    ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }>
                    {p.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
